export type CareerContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list-section'; title: string; items: string[] }
  | { type: 'prose-section'; title: string; paragraphs: string[] };

const LIST_ITEM_RE = /^[-•*▪]\s+|^\d+[.)]\s+/;

function isHeading(line: string): boolean {
  const t = line.trim();
  if (t.length < 3 || t.length > 120) return false;
  if (/^[A-Z0-9\s&/\-–—().]+$/.test(t) && t === t.toUpperCase() && /[A-Z]/.test(t)) return true;
  if (/^[^:]+:\s*$/.test(t) && t.length <= 80) return true;
  if (/^(what you will do|about the role|responsibilities)(\s*:)?$/i.test(t)) return true;
  if (/^(requirements?\s*(?:&|and)\s*benefits?|must[\s-]?have\s+requirements?|nice[\s-]?to[\s-]?have|benefits?(?:\s*&\s*perks?)?|qualifications?|responsibilities?)(\s*:)?$/i.test(t)) {
    return true;
  }
  return false;
}

function isListSectionTitle(title: string): boolean {
  return /requirement|nice[\s-]?to[\s-]?have|benefit|must[\s-]?have|perk|qualification|what you will|responsibilit|about the role/i.test(
    title,
  );
}

function stripListMarker(line: string): string {
  return line.replace(LIST_ITEM_RE, '').trim();
}

/** Split inline headers only — avoid bare "BENEFITS"/"REQUIREMENTS" (breaks "REQUIREMENTS & BENEFITS"). */
const INLINE_HEADER_RE =
  /\s+(?=(?:What you will do:|REQUIREMENTS\s*(?:&|AND)\s*BENEFITS|MUST-HAVE\s+REQUIREMENTS|NICE-TO-HAVE|BENEFITS(?:\s*&\s*PERKS)?|QUALIFICATIONS|RESPONSIBILITIES|ABOUT\s+THE\s+ROLE)\s*:?\s*)/gi;

/** Normalize pasted JD text — break inline headers onto their own lines. */
function normalizeDescription(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(INLINE_HEADER_RE, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Turn a plain-text JD (with inline headings) into structured blocks for display. */
export function parseCareerDescription(text: string): CareerContentBlock[] {
  const lines = normalizeDescription(text)
    .split(/\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const blocks: CareerContentBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isHeading(line)) {
      const title = line.replace(/:+\s*$/, '').trim();
      const items: string[] = [];
      const paragraphs: string[] = [];
      let hadListMarkers = false;
      i++;

      while (i < lines.length && !isHeading(lines[i])) {
        const current = lines[i];
        if (LIST_ITEM_RE.test(current)) {
          hadListMarkers = true;
          items.push(stripListMarker(current));
        } else if (isListSectionTitle(title) || items.length > 0) {
          items.push(current);
        } else {
          paragraphs.push(current);
        }
        i++;
      }

      const proseTitle = /^what you will|responsibilit|about the role/i.test(title);

      if (items.length > 0) {
        if (proseTitle && !hadListMarkers) {
          blocks.push({ type: 'prose-section', title, paragraphs: items });
        } else {
          blocks.push({ type: 'list-section', title, items });
        }
      } else if (paragraphs.length > 0) {
        blocks.push({ type: 'prose-section', title, paragraphs });
      }
      continue;
    }

    const paragraphLines: string[] = [line];
    i++;
    while (i < lines.length && !isHeading(lines[i])) {
      paragraphLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'paragraph', text: paragraphLines.join(' ') });
  }

  return blocks;
}
